/**
 * Infer pipeline identity from alert metadata and build lineage-aware RCA prompts.
 */

const DEFAULT_REGION = "us-east-1";

export function generatePipelineId(pipelineType, name, region, resourceId) {
  const pt = pipelineType || "glue";
  const nm = name || "unknown";
  const rg = region || DEFAULT_REGION;
  if (pt === "emr" && resourceId) {
    return `${pt}_${nm}_${resourceId}_${rg}`.replace(/ /g, "_").replace(/\//g, "_").replace(/:/g, "_");
  }
  return `${pt}_${nm}_${rg}`.replace(/ /g, "_").replace(/\//g, "_").replace(/:/g, "_");
}

function firstCapture(expr, patterns) {
  for (const pattern of patterns) {
    const match = expr.match(pattern);
    if (match?.[1]) return match[1].trim();
  }
  return null;
}

function inferTypeFromMetric(expr) {
  const lower = String(expr || "").toLowerCase();
  if (lower.includes("aws_glue_")) return "glue";
  if (lower.includes("aws_sfn_") || lower.includes("aws_states_")) return "step_functions";
  if (lower.includes("aws_emr_") || lower.includes("aws_elasticmapreduce_")) return "emr";
  if (lower.includes("aws_amazonmwaa_") || lower.includes("aws_mwaa_")) return "airflow";
  return null;
}

function stateMachineNameFromArn(arnOrName) {
  const value = String(arnOrName || "");
  const slash = value.lastIndexOf(":");
  if (slash >= 0) return value.slice(slash + 1);
  const pathSlash = value.lastIndexOf("/");
  if (pathSlash >= 0) return value.slice(pathSlash + 1);
  return value;
}

/**
 * Best-effort pipeline resolution from alert title, labels, and query expression.
 */
export function inferPipelineFromAlert(alert) {
  const expr =
    alert?.data?.find((item) => item?.model?.expr)?.model?.expr ||
    alert?.data?.[0]?.model?.expr ||
    alert?.expression ||
    alert?.metricExpression ||
    "";
  const labels = {
    ...(alert?.labels || {}),
    ...(alert?.data?.[0]?.labels || {}),
  };

  if (labels.pipeline_id) {
    return {
      pipelineId: labels.pipeline_id,
      pipelineName: labels.pipeline_name || labels.pipelineName || null,
      pipelineType: labels.pipeline_type || labels.pipelineType || null,
      region: labels.region || labels.pipeline_region || DEFAULT_REGION,
    };
  }

  const region =
    labels.region ||
    firstCapture(expr, [/region="([^"]+)"/i, /dimension_Region="([^"]+)"/i]) ||
    DEFAULT_REGION;

  const jobName =
    labels.jobname ||
    labels.JobName ||
    labels.job_name ||
    firstCapture(expr, [
      /dimension_JobName="([^"]+)"/i,
      /JobName="([^"]+)"/i,
      /job_name="([^"]+)"/i,
    ]);

  if (jobName) {
    return {
      pipelineId: generatePipelineId("glue", jobName, region),
      pipelineName: jobName,
      pipelineType: "glue",
      region,
    };
  }

  const stateMachine =
    labels.statemachine ||
    labels.StateMachineArn ||
    firstCapture(expr, [
      /dimension_StateMachineArn=~"([^"]+)"/i,
      /dimension_StateMachineArn="([^"]+)"/i,
      /StateMachineArn=~"([^"]+)"/i,
      /StateMachineArn="([^"]+)"/i,
    ]);

  if (stateMachine) {
    const name = stateMachineNameFromArn(stateMachine);
    return {
      pipelineId: generatePipelineId("step_functions", name, region),
      pipelineName: name,
      pipelineType: "step_functions",
      region,
    };
  }

  const clusterId =
    labels.jobflowid ||
    labels.JobFlowId ||
    firstCapture(expr, [
      /dimension_JobFlowId="([^"]+)"/i,
      /JobFlowId="([^"]+)"/i,
    ]);

  const clusterName =
    labels.cluster_name ||
    labels.clusterName ||
    firstCapture(expr, [/cluster_name="([^"]+)"/i]);

  if (clusterId || clusterName) {
    const name = clusterName || clusterId;
    return {
      pipelineId: generatePipelineId("emr", name, region, clusterId || undefined),
      pipelineName: name,
      pipelineType: "emr",
      region,
      resourceId: clusterId || null,
    };
  }

  const environment =
    labels.environment ||
    labels.Environment ||
    firstCapture(expr, [
      /dimension_Environment="([^"]+)"/i,
      /Environment="([^"]+)"/i,
    ]);

  const dagId =
    labels.dag_id ||
    labels.dag ||
    labels.dagId ||
    firstCapture(expr, [/dag_id="([^"]+)"/i, /dag="([^"]+)"/i]);

  if (dagId) {
    return {
      pipelineId: generatePipelineId("airflow", dagId, region),
      pipelineName: dagId,
      pipelineType: "airflow",
      region,
      environmentName: environment || null,
    };
  }

  if (environment) {
    const title = String(alert?.title || "").toLowerCase();
    const dagFromTitle = title.match(/([a-z0-9_]+_dag)/i)?.[1];
    if (dagFromTitle) {
      return {
        pipelineId: generatePipelineId("airflow", dagFromTitle, region),
        pipelineName: dagFromTitle,
        pipelineType: "airflow",
        region,
        environmentName: environment,
      };
    }
  }

  const inferredType = inferTypeFromMetric(expr);
  const title = String(alert?.title || "");
  const titleName =
    title.match(/(?:glue|job|dag|workflow|cluster)\s+["']?([^"']+)["']?/i)?.[1] ||
    title.match(/:\s*(.+)$/i)?.[1]?.trim();

  if (inferredType && titleName) {
    return {
      pipelineId: generatePipelineId(inferredType, titleName, region),
      pipelineName: titleName,
      pipelineType: inferredType,
      region,
    };
  }

  return null;
}

export function buildLineageAwareRcaPrompt(alert, graph) {
  const isAmp = alert?.platform === "amp";
  const expr =
    alert?.data?.[0]?.model?.expr ||
    alert?.expression ||
    alert?.metricExpression ||
    "";
  const description =
    alert?.annotations?.description ||
    alert?.annotations?.summary ||
    "";
  const base = [
    "Investigate this alert and determine root cause plus downstream impact.",
    `Alert: "${alert?.title || "Unknown"}".`,
    isAmp ? "Platform: Amazon Managed Prometheus (AMP)." : "Platform: Grafana.",
    isAmp && alert?.ruleGroup ? `AMP rule group: ${alert.ruleGroup}.` : "",
    alert?.severity ? `Severity: ${alert.severity}.` : "",
    description ? `Description: ${description}.` : "",
    expr ? `Rule/expression: ${expr}.` : "",
    isAmp && alert?.labels?.clairai_receiver
      ? `Notification receiver: ${alert.labels.clairai_receiver}.`
      : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (graph?.rca_context) {
    return `${base}\n\n${graph.rca_context}`;
  }
  return base;
}

export async function resolveLineageContextForAlert(alert, { clientId, awsAccountId, fetchGraph }) {
  if (!clientId || !awsAccountId || typeof fetchGraph !== "function") {
    return null;
  }

  const inferred = inferPipelineFromAlert(alert);
  if (!inferred?.pipelineId) {
    return null;
  }

  try {
    const graph = await fetchGraph(inferred.pipelineId, clientId, awsAccountId, {
      pipelineName: inferred.pipelineName,
      pipelineType: inferred.pipelineType,
      pipelineRegion: inferred.region,
    });
    return { inferred, graph };
  } catch (error) {
    console.warn("[resolveLineageContextForAlert]", error);
    return { inferred, graph: null, error };
  }
}
